import { apiGet, apiPost } from './utils';
import type {
  CalendarEvent,
  CalendarListItem,
  HasCalendarResponse,
  SyncedCalendarsResponse,
} from '../types/calendar';

export interface CalendarEventParams {
  associated_type: string;
  associated_uuid: string;
}

export interface CreateEventParams extends CalendarEventParams {
  summary: string;
  description: string;
  calendar_id: string;
  location?: string;
  start: string;
  end: string;
}

export interface UpdateEventParams extends CalendarEventParams {
  id: string;
  summary: string;
  description: string;
  location?: string;
  start: string;
  end: string;
}

export interface DeleteEventParams extends CalendarEventParams {
  event_id: string;
  calendar_id: string;
}

export interface ListEventsParams extends CalendarEventParams {
  start: string;
  end: string;
}

export const calendarEventApi = {
  /**
   * List calendar events for a date range
   */
  async list(params: ListEventsParams): Promise<CalendarEvent[]> {
    return apiGet<CalendarEvent[]>('/calendar-event/list', params as unknown as Record<string, string>);
  },

  /**
   * Create a new calendar event
   */
  async create(params: CreateEventParams): Promise<CalendarEvent> {
    return apiPost<CalendarEvent>('/calendar-event/create', params as unknown as Record<string, unknown>);
  },

  /**
   * Update an existing calendar event
   */
  async update(params: UpdateEventParams): Promise<CalendarEvent> {
    return apiGet<CalendarEvent>('/calendar-event/update', params as unknown as Record<string, string>);
  },

  /**
   * Delete a calendar event
   */
  async delete(params: DeleteEventParams): Promise<void> {
    await apiPost('/calendar-event/delete', params as unknown as Record<string, unknown>);
  },

  /**
   * Get synced calendars for user
   */
  async getSyncedCalendars(params: CalendarEventParams): Promise<CalendarListItem[]> {
    const response = await apiGet<SyncedCalendarsResponse>(
      '/calendar-event/google-synced-calendars',
      params as unknown as Record<string, string>
    );
    // Combine calendar_list_items and calendars into a single list
    return Object.keys(response.calendar_list_items).map((id) => ({
      ...response.calendar_list_items[id],
      ...response.calendars[id],
    }));
  },

  /**
   * Check if user has calendar connected
   */
  async hasCalendar(
    params: CalendarEventParams
  ): Promise<{ hasCalendar: boolean; timezone?: string; preferences?: string }> {
    const response = await apiGet<HasCalendarResponse>('/calendar-event/has-calendar', params as unknown as Record<string, string>);
    if (response.has_calendar) {
      return {
        hasCalendar: true,
        timezone: response.timezone_city,
        preferences: response.calendar_preferences,
      };
    }
    return { hasCalendar: false };
  },
};
